// src/lib/admin-utils.ts
import type { Document, ConfigProps } from '@/types';
import { Template, Assistant, AccessControl } from '@/lib/firebase';
import { 
  StructuredInstructionsType, 
  parseInstructions, 
  combineInstructions 
} from '@/components/StructuredInstructions';

export type ExtendedConfig = Partial<ConfigProps & { 
  templateId: string;
  assistantId: string;
  toolConfigs?: Record<string, Record<string, any>>;
  structuredInstructions?: StructuredInstructionsType;
  accessControl?: AccessControl; 
}>;

/**
 * Creates a config object from a template
 */
export function createConfigFromTemplate(
  template: Template,
  preserveExistingValues: Partial<ExtendedConfig> = {}
): ExtendedConfig {
  // Parse instructions into structured format
  const structuredInstructions = parseInstructions(template.defaultInstructions);
  
  // Default to domain access if possible or inherit from preserveExistingValues
  const accessControl = preserveExistingValues.accessControl || { type: 'domain' };
  
  return {
    // Template values first (completely replace these)
    templateId: template.templateId,
    name: preserveExistingValues.name || template.name,
    avatar: template.avatar,
    initialMessage: template.defaultMessage,
    initialInstructions: template.defaultInstructions,
    structuredInstructions,
    tools: template.tools || [],
    toolConfigs: template.toolConfigs || {}, // Completely replace toolConfigs
    initialDocuments: [], // Reset documents when using template
    accessControl,
    // Only preserve specific admin fields
    adminEmail: preserveExistingValues.adminEmail,
    senderName: preserveExistingValues.senderName,
  };
}

/**
 * Creates a config object from an assistant
 */
export function createConfigFromAssistant(assistant: Partial<Assistant> & { 
  assistantId: string;
  templateId: string;
  name: string;
  avatar: string;
  ownerEmail: string;
}
): ExtendedConfig {
  // Parse instructions into structured format
  const structuredInstructions = parseInstructions(assistant.initialInstructions);
  
  return {
    templateId: assistant.templateId,
    assistantId: assistant.assistantId,
    name: assistant.name,
    avatar: assistant.avatar,
    adminEmail: assistant.ownerEmail,
    initialMessage: assistant.initialMessage || '',
    initialInstructions: assistant.initialInstructions || '',
    structuredInstructions,
    tools: assistant.tools || [],
    toolConfigs: assistant.toolConfigs || {},
    // Ensure we have a valid Document array
    initialDocuments: (assistant.initialDocuments || []).filter((doc): doc is Document => doc !== undefined),
    accessControl: assistant.accessControl
  };
}

/**
 * Creates a clean update object from the current config
 */
export function createUpdateConfig(
    config: ExtendedConfig, 
    userId: string,
    preserveDocuments: boolean = true
  ): any {

    // Use the provided initialInstructions if available, otherwise combine structured instructions
    const combinedInstructions = config.initialInstructions || combineInstructions(config.structuredInstructions || {
      goal: '',
      tone: '',
      persona: '',
      constraints: '',
      outputFormat: ''
    });
    
    // Create a clean access control object for Firestore
    let firestoreAccessControl: AccessControl = { type: 'private' };
    
    if (config.accessControl) {
      if (config.accessControl.type === 'public') {
        firestoreAccessControl = { type: 'public' };
      } else if (config.accessControl.type === 'domain') {
        firestoreAccessControl = {
          type: 'domain'
        };
      } else if (config.accessControl.type === 'domains' && config.accessControl.domains) {
        firestoreAccessControl = {
          type: 'domains',
          domains: config.accessControl.domains
        };
      } else if (config.accessControl.type === 'specific' && config.accessControl.emails) {
        firestoreAccessControl = {
          type: 'specific',
          emails: config.accessControl.emails
        };
      } else if (config.accessControl.type === 'group' && config.accessControl.groups) {
        firestoreAccessControl = {
          type: 'group',
          groups: config.accessControl.groups
        };
      } else {
        firestoreAccessControl = { type: 'private' };
      }
    }
    
    const result: any = {
      templateId: config.templateId!,
      name: config.name,
      avatar: config.avatar,
      ownerId: userId,
      ownerEmail: config.adminEmail!,
      initialMessage: config.initialMessage,
      initialInstructions: combinedInstructions,
      initialDocuments: preserveDocuments ? config.initialDocuments || [] : [],
      tools: config.tools || [],
      toolConfigs: config.toolConfigs || {},
      accessControl: firestoreAccessControl,
      updatedAt: Date.now()
    };

    // Include structuredInstructions if provided
    if (config.structuredInstructions) {
      result.structuredInstructions = config.structuredInstructions;
    }

    // Include personaTemplateId if provided
    if ((config as any).personaTemplateId) {
      result.personaTemplateId = (config as any).personaTemplateId;
    }

    return result;
  }

/**
 * Creates a duplicate config from an existing assistant
 */
export function createDuplicateConfig(assistant: Assistant, userId: string, userEmail: string): any {
  return {
    templateId: assistant.templateId,
    name: assistant.name,
    avatar: assistant.avatar,
    ownerId: userId,
    ownerEmail: userEmail,
    initialMessage: assistant.initialMessage,  // Copy the greeting from parent
    initialInstructions: assistant.initialInstructions,
    tools: assistant.tools || [],
    toolConfigs: assistant.toolConfigs || {},
    initialDocuments: [], // Start with empty documents, they'll be copied separately
    accessControl: assistant.accessControl || { type: 'domain' }, // Preserve access settings or default to private
    emailSubscriptions: [], // Always start with empty subscriptions for duplicates
    createdAt: Date.now(),
    updatedAt: Date.now()
  };
}